import React, { Fragment, useState, useRef, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import PageTitle from "../../../layouts/PageTitle";

import { useAuth } from "../../../../context/AuthContext";
import { db, storage } from "../../../../firebase";
import {
  setDoc,
  updateDoc,
  doc,
  collection,
  getDoc,
} from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import { notifyTopRight, notifyWarning, notifyError } from "../../toast/SetToast";
import Toast from "../../toast/SetToast";
import { CpapTrialTicketForm } from "./CpapTrialTicketForm";

const CpapTrialTicketCreate = (props) => {
  const { currentUser } = useAuth();
  const [fileNames, setFilenames] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [serviceOrderNumber, setServiceOrderNumber] = useState(null);
  const [patientData, setPatientData] = useState({
    hn: "",
    brand: "",
    model: "",
    cpapType: "",
  });

  const inputRef = useRef({
    date: "",
    serviceType: "",
    sn: "",
    detail: "",
    accessories: "",
    description: "",
    image: "",
    receiveBy: "",
    serviceStatus: "",
    serviceDetail: "",
    amount: "",
    completionDate: "",
    serviceBy: "",
    note: "",
  });

  const handleChange = (name) => (e) => {
    if (name === "image") {
      for (let i = 0; i < e.target.files.length; i++) {
        const f = e.target.files[i];
        setSelectedFiles((prevState) => [...prevState, f]);
        setFilenames((prevState) => [...prevState, f.name]);
      }
    } else if (name === "sn") {
      setSelected(e.target.value);
      inputRef.current[name] = e.target.value;
    } else {
      inputRef.current[name] = e.target.value;
    }
  };

  const handleProductFound = (productData) => {
    setPatientData((prev) => ({
      ...prev,
      hn: productData.hn || "",
      brand: productData.brand || "",
      model: productData.model || "",
      cpapType: productData.cpapType || "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = {
      ...patientData,
      serviceOrderNumber: serviceOrderNumber,
      date: inputRef.current.date,
      serviceType: inputRef.current.serviceType,
      sn: inputRef.current.sn,
      accessories: inputRef.current.accessories,
      description: inputRef.current.description,
      image: fileNames,
      receiveBy: inputRef.current.receiveBy,
      serviceStatus: inputRef.current.serviceStatus,
      serviceDetail: inputRef.current.serviceDetail,
      completionDate: inputRef.current.completionDate,
      serviceBy: inputRef.current.serviceBy,
      amount: inputRef.current.amount || "",
      note: inputRef.current.note || "",
      hasExternalRepair: false,
      createdBy: currentUser.displayName,
      updatedAt: new Date(),
      createdAt: new Date(),
    };

    const docRef = doc(collection(db, "cpapTrialServiceTickets"));

    await setDoc(docRef, data).then(async () => {
      await updateDoc(doc(db, "cpapTrialServiceTickets", docRef.id), {
        ticketId: docRef.id,
      });

      // Update CPAP device status
      if (inputRef.current.sn) {
        const cpapRef = doc(db, "cpaps", inputRef.current.sn);
        const cpapSnap = await getDoc(cpapRef);
        if (cpapSnap.exists()) {
          await updateDoc(cpapRef, {
            Status: "ไม่ว่าง",
            Activity: "ส่งซ่อม",
            UpdatedDateTime: new Date(),
          });
        }
      }

      // Upload images
      const promises = [];
      selectedFiles.map((file) => {
        const storageRef = ref(
          storage,
          `cpapTrialServiceImage/${docRef.id}/${file.name}`
        );
        const uploadTask = uploadBytes(storageRef, file);
        promises.push(uploadTask);
      });
      Promise.all(promises);
      notifyTopRight("Create success");
    });

    // Reset form
    inputRef.current = {
      date: "",
      serviceType: "",
      sn: "",
      detail: "",
      accessories: "",
      description: "",
      image: "",
      receiveBy: "",
      serviceStatus: "",
      serviceDetail: "",
      amount: "",
      completionDate: "",
      serviceBy: "",
      note: "",
    };

    setServiceOrderNumber(null);
    setSelectedFiles([]);
    setFilenames([]);
    setPatientData({
      hn: "",
      brand: "",
      model: "",
      cpapType: "",
    });

    // Update next service order number
    const { sequence } = await getNextSequence();
    const newSequence = parseInt(sequence, 10) + 1;
    await updateSequence(newSequence);

    e.target.reset();
  };

  async function getNextSequence() {
    const sequenceRef = doc(db, "sequenceTracker", "cpapTrialServiceSequence");
    const currentDate = new Date();
    const thaiYear = currentDate.getFullYear() + 543;

    try {
      const sequenceDoc = await getDoc(sequenceRef);

      if (!sequenceDoc.exists()) {
        await setDoc(sequenceRef, { current: 1, year: thaiYear });
        return { sequence: "001", year: thaiYear };
      }

      const data = sequenceDoc.data();
      let currentSequence;

      if (data.year !== thaiYear) {
        currentSequence = 1;
        await setDoc(sequenceRef, { current: 1, year: thaiYear });
      } else {
        currentSequence = data.current;
      }

      return { sequence: currentSequence.toString().padStart(3, "0"), year: thaiYear };
    } catch (e) {
      console.error("Failed to get sequence: ", e);
      throw e;
    }
  }

  async function updateSequence(newSequence) {
    const sequenceRef = doc(db, "sequenceTracker", "cpapTrialServiceSequence");
    const currentDate = new Date();
    const thaiYear = currentDate.getFullYear() + 543;

    try {
      await updateDoc(sequenceRef, { current: newSequence, year: thaiYear });
    } catch (e) {
      console.error("Failed to update sequence: ", e);
      throw e;
    }
  }

  const generateId = async () => {
    const { sequence, year } = await getNextSequence();
    const yearStr = year.toString().slice(2);
    return `BD${yearStr}${sequence}`;
  };

  useEffect(() => {
    generateId().then((id) => setServiceOrderNumber(id));
  }, []);

  return (
    <Fragment>
      <PageTitle
        activeMenu="Create CPAP Trial Maintenance Ticket"
        motherMenu="CPAP Trial"
        motherLink="/cpap-trial-service-list"
      />
      <Toast />
      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Create CPAP Trial Maintenance Ticket</h4>
            </div>

            <div className="card-body">
              <div className="form-validation">
                <form
                  className="form-valide"
                  action="#"
                  method="post"
                  onSubmit={handleSubmit}
                >
                  <div className="row">
                    <div className="col-xl-6">
                      <CpapTrialTicketForm
                        onChange={handleChange}
                        fileNames={fileNames}
                        initailValues={null}
                        product={selected}
                        defaultfiles={[]}
                        patientData={patientData}
                        onProductFound={handleProductFound}
                        serviceOrderNumber={serviceOrderNumber}
                      />
                      <br /> <br />
                      <div className="form-group row">
                        <div className="col-lg-8 ml-auto">
                          <button type="submit" className="btn btn-primary">
                            Submit
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default CpapTrialTicketCreate;
